import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useVertraege, useFristenUebersicht, useKuendigungen, useEinsaetzeNachzutragen, datum, eur,
} from "./useVertraege";
import { useEinzugVorschau, useEinzugLaeufe } from "./useLastschrift";
import { periodeDe } from "../lib/zahl";

/**
 * Aufgaben — was in der Pflege zu tun ist, aus den Abfragen, die es schon gibt.
 *
 * Bis 09.10.2026 hieß die Seite „Heute" und zeigte Kennzahlen und Wegweiser;
 * die Arbeit stand verteilt auf Fristen, Einsätze und Lastschrift. Jetzt steht
 * sie hier, jede Zeile mit einem Weg, sie zu erledigen. Der Name ist der aus
 * Kontor, damit beide Apps dasselbe Wort für dieselbe Sache haben.
 *
 * Drei Stufen: „jetzt" (rot/gelb, zählt in die Zahl an der Leiste),
 * „bald" (grau) und „warten" (liegt beim Kunden).
 */
export type Ton = "rot" | "gelb" | "gruen" | "grau";
export type Aufgabe = {
  key: string;
  ton: Ton;
  stufe: "neu" | "jetzt" | "bald" | "warten";
  /** Nur bei „neu": die Vertrags-ID, mit der die Meldung als gesehen gilt. */
  gesehenId?: string;
  titel: string;
  grund: string;
  ziel: string;
  knopf: string;
};

/**
 * „Neu abgeschlossen" ist eine Meldung, keine Aufgabe: sie steht da, bis man
 * sie gesehen hat (je Gerät gemerkt) — höchstens 14 Tage nach der Zustimmung.
 * Seit 09.10.2026 wird ein Vertrag mit der Zustimmung von selbst aktiv;
 * Benjamin bekommt dazu eine Mail und diese Meldung in der App.
 */
const GESEHEN_KEY = "pf-neu-gesehen";
const GESEHEN_EVENT = "pf-neu-gesehen";
function gesehenLesen(): string[] {
  try { return JSON.parse(localStorage.getItem(GESEHEN_KEY) || "[]") as string[]; } catch { return []; }
}
export function useGesehen(): { gesehen: Set<string>; markieren: (id: string) => void } {
  const [liste, setListe] = useState<string[]>(gesehenLesen);
  useEffect(() => {
    const neu = () => setListe(gesehenLesen());
    window.addEventListener(GESEHEN_EVENT, neu);
    return () => window.removeEventListener(GESEHEN_EVENT, neu);
  }, []);
  const markieren = useCallback((id: string) => {
    const neu = [...new Set([...gesehenLesen(), id])].slice(-200);
    try { localStorage.setItem(GESEHEN_KEY, JSON.stringify(neu)); } catch { /* ohne Speicher bleibt sie stehen */ }
    setListe(neu);
    window.dispatchEvent(new Event(GESEHEN_EVENT));
  }, []);
  return { gesehen: new Set(liste), markieren };
}

export function useAufgaben(): { liste: Aufgabe[]; laedt: boolean } {
  const vertraege = useVertraege();
  const fristen = useFristenUebersicht();
  const kuendigungen = useKuendigungen(true);
  const nachweise = useEinsaetzeNachzutragen();
  const einzug = useEinzugVorschau();
  const laeufe = useEinzugLaeufe();
  const { gesehen } = useGesehen();

  const liste = useMemo(() => {
    const a: Aufgabe[] = [];
    const vor14 = Date.now() - 14 * 86_400_000;
    for (const v of vertraege.data || []) {
      if (v.status === "aktiv" && v.zugestimmt_am && new Date(v.zugestimmt_am).getTime() >= vor14
          && !gesehen.has(v.id)) {
        a.push({
          key: "neu-" + v.id, ton: "gruen", stufe: "neu", gesehenId: v.id,
          titel: `${v.kunde_name || "Ein Kunde"} hat zugestimmt — der Vertrag ist aktiv`,
          grund: `${v.nummer} · zugestimmt am ${datum(v.zugestimmt_am)}. Pflegeprojekt, Raten und die Termine `
            + "des ersten Jahres sind angelegt; die Termine stehen als Vorschlag im Planer.",
          ziel: `/vertraege/${v.id}`, knopf: "Ansehen",
        });
      }
    }
    for (const k of kuendigungen.data || []) {
      a.push({
        key: "kue-" + k.id, ton: "rot", stufe: "jetzt",
        titel: `Kündigung von ${k.kunde_name || k.erklaerer_name || "unbekannt"}`,
        grund: `Eingegangen am ${datum(k.eingang_am)} — sie wirkt mit dem Zugang. `
          + (k.vertrag_id ? "Vertragsende bestätigen." : "Erst einem Vertrag zuordnen."),
        ziel: "/fristen", knopf: "Bearbeiten",
      });
    }
    // Nur jüngere Läufe: ein alter Rückläufer ist längst bearbeitet und stünde
    // sonst für immer rot da.
    const grenze = Date.now() - 45 * 86_400_000;
    for (const l of laeufe.data || []) {
      if (l.zurueck > 0 && new Date(l.faellig_am).getTime() >= grenze) {
        a.push({
          key: "rueck-" + l.msg_id, ton: "rot", stufe: "jetzt",
          titel: l.zurueck === 1 ? "Eine Lastschrift kam zurück" : `${l.zurueck} Lastschriften kamen zurück`,
          grund: `Einzug vom ${datum(l.faellig_am)}. Die Rate ist wieder offen.`,
          ziel: "/zahlungen", knopf: "Ansehen",
        });
      }
    }
    for (const v of vertraege.data || []) {
      if (v.status === "zugestimmt") {
        a.push({
          key: "akt-" + v.id, ton: "gelb", stufe: "jetzt",
          titel: `${v.kunde_name || "Kunde"} hat zugestimmt — noch nicht aktiv`,
          grund: `${v.nummer} · die automatische Aktivierung hat nicht geklappt (Grund in der Büromail). Von Hand aktivieren.`,
          ziel: `/vertraege/${v.id}`, knopf: "Aktivieren",
        });
      }
    }
    const e = einzug.data;
    if (e?.einzugsweg === "datei" && e.posten.length > 0) {
      a.push({
        key: "einzug", ton: "gelb", stufe: "jetzt",
        titel: e.posten.length === 1 ? "Eine Rate einziehen" : `${e.posten.length} Raten einziehen`,
        grund: `Fällig bis ${periodeDe(e.bis_periode)}, zusammen ${eur(e.summe_brutto)}. Einzugsdatei erstellen und zur Sparkasse geben.`,
        ziel: "/zahlungen", knopf: "Einzugsdatei",
      });
    }
    for (const h of e?.hindernisse || []) {
      a.push({
        key: `hin-${h.vertrag_id}-${h.periode}`, ton: "gelb", stufe: "jetzt",
        titel: `Rate von ${h.kunde_name} hängt fest`,
        grund: `${periodeDe(h.periode)} · ${eur(h.betrag_brutto)} · ${h.grund}`,
        ziel: `/vertraege/${h.vertrag_id}`, knopf: "Öffnen",
      });
    }
    for (const v of vertraege.data || []) {
      if (v.status === "entwurf") {
        a.push({
          key: "ent-" + v.id, ton: "grau", stufe: "bald",
          titel: `Entwurf für ${v.kunde_name || "einen Kunden"}`,
          grund: `${v.positionen} ${v.positionen === 1 ? "Leistung" : "Leistungen"} · noch nicht angeboten.`,
          ziel: `/vertraege/neu?entwurf=${v.id}`, knopf: "Weiterbearbeiten",
        });
      }
    }
    for (const z of nachweise.data?.vertraege || []) {
      a.push({
        key: "nw-" + z.id, ton: "grau", stufe: "bald",
        titel: `${z.kunde_name || z.nummer}: ${z.offen === 1 ? "ein Einsatz" : `${z.offen} Einsätze`} ohne Nachweis`,
        grund: `Ältester Termin ${datum(z.aeltester)}. Ohne Eintrag fehlt er im Leistungsnachweis nach § 35a.`,
        ziel: `/vertraege/${z.id}#einsaetze`, knopf: "Nachtragen",
      });
    }
    for (const f of fristen.data?.laufen_aus || []) {
      a.push({
        key: "frist-" + f.id, ton: "grau", stufe: "bald",
        titel: `${f.kunde_name}: Kündigungstermin ${datum(f.fristen.naechster_kuendigungstermin)}`,
        grund: `${f.nummer} · noch ${f.fristen.tage_bis_kuendigungstermin} Tage.`,
        ziel: `/vertraege/${f.id}`, knopf: "Öffnen",
      });
    }
    const pa = fristen.data?.preisanpassung_faellig.length || 0;
    if (pa > 0) {
      a.push({
        key: "preis", ton: "grau", stufe: "bald",
        titel: pa === 1 ? "Ein Vertrag lässt sich im Preis anpassen" : `${pa} Verträge lassen sich im Preis anpassen`,
        grund: "Preisrunde vorbereiten: acht Wochen Vorlauf, über 5 % mit Sonderkündigungsrecht.",
        ziel: "/auswertung#preisrunde", knopf: "Preisrunde",
      });
    }
    for (const v of vertraege.data || []) {
      if (v.status === "angeboten") {
        a.push({
          key: "ang-" + v.id, ton: "grau", stufe: "warten",
          titel: `Angebot bei ${v.kunde_name || "Kunde"}`,
          grund: `${v.nummer} · ${v.monats_netto ? `${eur(v.monats_netto)} im Monat netto` : "ohne Rate"}.`,
          ziel: `/vertraege/${v.id}`, knopf: "Öffnen",
        });
      }
    }
    return a;
  }, [vertraege.data, fristen.data, kuendigungen.data, nachweise.data, einzug.data, laeufe.data, gesehen]);

  return { liste, laedt: vertraege.isLoading || fristen.isLoading };
}

/** Zahl an „Aufgaben" in der Leiste: was jetzt dran ist und neue Abschlüsse. */
export function useAufgabenAnzahl(): number {
  const { liste } = useAufgaben();
  return liste.filter(a => a.stufe === "jetzt" || a.stufe === "neu").length;
}
